/**
 * Portfolio Summary component showing total assets, profit/loss, current value
 * Redesigned with personal finance app inspiration
 */

import { formatVND } from '@/utils/currencyUtils'
import { calculatePortfolioSummary } from '@/utils/portfolioUtils'
import { SummaryCard } from '@/components/common/SummaryCard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { 
  IconCurrencyDollar, 
  IconTrendingUp, 
  IconTrendingDown, 
  IconWallet, 
  IconReceipt,
  IconCoins,
  IconChartLine
} from '@tabler/icons-react'
import type { GoldAsset } from '@/types/asset'
import type { SaleTransaction } from '@/types/sale'
import type { GoldPriceHistory } from '@/types/price'

interface PortfolioSummaryProps {
  assets: GoldAsset[]
  sales: SaleTransaction[]
  latestPrices: Map<number, GoldPriceHistory>
}

export function PortfolioSummary({ assets, sales, latestPrices }: PortfolioSummaryProps) {
  const summary = calculatePortfolioSummary(assets, sales, latestPrices)
  const holdingAssetsList = assets.filter((a) => a.status === 'holding')

  // Tính tổng lượng và tỷ lệ phần trăm theo loại vàng (đơn vị: lượng)
  const { totalLuongHolding, quantityBreakdownData } = (() => {
    const totalLuong = holdingAssetsList.reduce((sum, a) => sum + (a.quantity || 0), 0)
    if (totalLuong <= 0) {
      return { totalLuongHolding: 0, quantityBreakdownData: [] }
    }

    const byType = holdingAssetsList.reduce<Record<string, number>>((acc, a) => {
      const name = a.goldTypeName || `Loại #${a.goldTypeId}`
      acc[name] = (acc[name] || 0) + (a.quantity || 0)
      return acc
    }, {})

    const breakdownData = Object.entries(byType)
      .sort((a, b) => b[1] - a[1])
      .map(([name, qtyLuong]) => ({
        name,
        quantity: qtyLuong,
        percent: (qtyLuong / totalLuong) * 100,
      }))

    return {
      totalLuongHolding: totalLuong,
      quantityBreakdownData: breakdownData,
    }
  })()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tóm tắt Danh mục</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 min-w-0">
          <SummaryCard
            title="Tổng số Tài sản"
            value={`${totalLuongHolding.toLocaleString('vi-VN', { maximumFractionDigits: 2 })} lượng`}
            icon={IconCoins}
            variant="neutral"
            breakdown={
              quantityBreakdownData.length > 0
                ? {
                    data: quantityBreakdownData,
                    totalQuantity: totalLuongHolding,
                  }
                : undefined
            }
          />
          <SummaryCard
            title="Tổng Chi phí"
            value={formatVND(summary.totalCost)}
            subtitle="Tổng số tiền đã đầu tư"
            icon={IconCurrencyDollar}
            variant="neutral"
          />
          <SummaryCard
            title="Giá trị Hiện tại"
            value={formatVND(summary.currentValue)}
            subtitle="Giá trị thị trường hiện tại"
            icon={IconWallet}
            variant={summary.currentValue > 0 ? 'default' : 'neutral'}
          />
          <SummaryCard
            title="Tổng Doanh thu"
            value={formatVND(summary.totalRevenue)}
            subtitle="Từ các giao dịch bán"
            icon={IconReceipt}
            variant="neutral"
          />
          <SummaryCard
            title="Lợi nhuận / Lỗ"
            value={formatVND(Math.abs(summary.totalProfitLoss))}
            subtitle="Tổng lợi nhuận hoặc lỗ"
            change={{
              value: summary.totalProfitLoss,
              percent: summary.profitLossPercent,
            }}
            icon={summary.totalProfitLoss >= 0 ? IconTrendingUp : IconTrendingDown}
            variant={summary.totalProfitLoss >= 0 ? 'positive' : 'negative'}
          />
          <SummaryCard
            title="Tỷ lệ Lợi nhuận"
            value={`${summary.profitLossPercent >= 0 ? '+' : ''}${summary.profitLossPercent.toFixed(2)}%`}
            subtitle="Phần trăm so với chi phí"
            change={{
              value: summary.totalProfitLoss,
              percent: summary.profitLossPercent,
            }}
            icon={summary.profitLossPercent >= 0 ? IconChartLine : IconTrendingDown}
            variant={summary.profitLossPercent >= 0 ? 'positive' : 'negative'}
          />
        </div>
      </CardContent>
    </Card>
  )
}

